import { removeGroupingSpaces } from 'utils/numberUtils/decimalInput'
import { getDecimalSeparator } from 'utils/numberUtils/numberUtils'
import type { ElplanVolumeReading } from './ElplanEditor.types'

const ELPLAN_VOLUME_DECIMALS = 1

const VOLUME_CHARACTER = /^[\d.,\-\u2212]$/

const VOLUME_PATTERN = /^[-\u2212]?(\d+|\d{1,3}([ \u00A0\u202F]\d{3})+)([.,]\d+)?$/

export function readElplanVolume(text: string, precision: 'one' | 'any'): ElplanVolumeReading {
  const trimmed = text.trim()
  if (trimmed === '') {
    return { kind: 'blank' }
  }
  if (!VOLUME_PATTERN.test(trimmed)) {
    return { kind: 'fault', fault: 'not-a-number' }
  }

  const decimal = removeGroupingSpaces(trimmed).replace('\u2212', '-').replace(',', '.')
  const volumeMw = Number(decimal)
  if (!Number.isFinite(volumeMw)) {
    return { kind: 'fault', fault: 'not-a-number' }
  }
  if (precision === 'one' && getFractionDigits(decimal).length > ELPLAN_VOLUME_DECIMALS) {
    return { kind: 'fault', fault: 'too-many-decimals' }
  }

  return { kind: 'value', volumeMw }
}

export function isElplanVolumeCharacter(character: string): boolean {
  return VOLUME_CHARACTER.test(character)
}

export function roundElplanVolumeMean(volumesMw: readonly number[]): number {
  const decimals = volumesMw.map(formatElplanVolumeText)
  const scale = Math.max(0, ...decimals.map((decimal) => getFractionDigits(decimal).length))
  const sum = decimals.reduce((total, decimal) => total + toScaledInteger(decimal, scale), 0n)
  const roundedUnits = divideRoundingHalfAwayFromZero(
    sum * 10n ** BigInt(ELPLAN_VOLUME_DECIMALS),
    BigInt(volumesMw.length) * 10n ** BigInt(scale)
  )

  return Number(roundedUnits) / 10 ** ELPLAN_VOLUME_DECIMALS
}

export function toElplanVolumeCellText(volumeMw: number): string {
  return formatElplanVolumeText(volumeMw).replace('.', getDecimalSeparator())
}

function formatElplanVolumeText(volumeMw: number): string {
  const text = String(volumeMw)
  return text.includes('e') ? expandExponent(text) : text
}

function getFractionDigits(decimal: string): string {
  return decimal.split('.')[1] ?? ''
}

function toScaledInteger(decimal: string, scale: number): bigint {
  const [integerDigits, fractionDigits = ''] = decimal.split('.')
  return BigInt(integerDigits + fractionDigits.padEnd(scale, '0'))
}

function divideRoundingHalfAwayFromZero(dividend: bigint, divisor: bigint): bigint {
  const quotient = dividend / divisor
  const remainder = dividend % divisor
  const absoluteRemainder = remainder < 0n ? -remainder : remainder
  if (2n * absoluteRemainder < divisor) {
    return quotient
  }

  return dividend < 0n ? quotient - 1n : quotient + 1n
}

function expandExponent(text: string): string {
  const [mantissa, exponent] = text.split('e')
  const sign = mantissa.startsWith('-') ? '-' : ''
  const [integerDigits, fractionDigits = ''] = mantissa.replace('-', '').split('.')
  const digits = integerDigits + fractionDigits
  const pointIndex = integerDigits.length + Number(exponent)

  return pointIndex <= 0
    ? `${sign}0.${'0'.repeat(-pointIndex)}${digits}`
    : `${sign}${digits}${'0'.repeat(pointIndex - digits.length)}`
}
